import React, { useState } from 'react';
import { ConfirmButton, InputIdentifier } from '../../VehicleIdentifier.styles';
import { BlockingErrorMessage, ErrorMessage, ManualForm, ManualFormLabel } from './Vessel.styles';
import Vessel from './models/vessel.entity';
import VesselData from './models/vessel-data.entity';
import vesselServices from './services/vessel.services';
import { AxiosResponse } from 'axios';
import { LocalStoreKeysEnum } from '../../../../models/enums/localstore-keys.enum';
import authService from '../../../../auth/auth.service';
import AuthUser from '../../../../auth/models/auth-user.entity';
import { useHistory } from 'react-router-dom';
import PlateInfo from '../../../../models/plate-info.entity';
import storageService from '../../../../services/storage.service';
import { CircularProgress } from '@mui/material';

export function VesselForm() {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [blockingError, setBlockingError] = useState('');
  const [errors, setErrors] = useState({
    identification: '',
    vehicle_type: '',
    brand: '',
    model: '',
    version: '',
    year_manufacture: '',
  });
  const [showManualRegister] = useState(true);

  const [vesselFormData, setVesselFormData] = useState<any>(
    {
      identification: '',
      model: '',
      brand: '',
      version: '',
      vehicle_type: '',
      year_manufacture: '',
    }
  );

  const onChangeVesselData = (e: any) => {
    const { name, value } = e.target;

    setVesselFormData((oldData: any) => ({
      ...oldData,
      [name]: value
    }));

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: ''
    }));
  }

  const getAuth = () => {
    authService.getToken()
      .then((result) => {
        const user: AuthUser = new AuthUser();
        user.accessToken = result.token;

        authService.saveAuth(user.formatAuthPayload());
      })
      .catch();
  }

  const goToInspection = () => {
    history.push('inspection');
  };

  const saveVehicleData = () => {
    const vessel = new Vessel();
    vessel.vesselData = new VesselData({
      identification: vesselFormData.identification,
      model: vesselFormData.model,
      brand: vesselFormData.brand,
      version: vesselFormData.version,
      vehicle_type: vesselFormData.vehicle_type,
      year_manufacture: vesselFormData.year_manufacture,
    });

    authService.getToken()
      .then((result) => {
        const user: AuthUser = new AuthUser();
        user.accessToken = result.token;

        authService.saveAuth(user.formatAuthPayload());
      })
      .catch(() => { })
      .finally(() => {
        vesselServices.saveVehicleData(vessel)
          .then((result: AxiosResponse) => {
            const { data } = result;
            const { inspection_value, inspection_id } = data;

            const inspectionValue = Array.isArray(inspection_value) ? null : inspection_value;

            const plateInfo = new PlateInfo();
            plateInfo.chassis = vesselFormData.identification;
            plateInfo.branchModel = `${vesselFormData.brand} ${vesselFormData.model}`.trim();

            storageService.setObject('plateInfo', plateInfo);
            localStorage.setItem(LocalStoreKeysEnum.LABEL_INSPECTION_PRICE, inspectionValue);
            localStorage.setItem(LocalStoreKeysEnum.LABEL_INSPECTION_ID, inspection_id);

            getAuth();
            goToInspection();
          }).catch((err) => {
            const status = err.response?.status;

            if (status === 422) {
              const responseErrors = err.response?.data?.errors || {};

              setErrors({
                identification: responseErrors["vehicle_data.identification"] ? responseErrors["vehicle_data.identification"][0] : '',
                brand: responseErrors["vehicle_data.brand"] ? responseErrors["vehicle_data.brand"][0] : '',
                model: responseErrors["vehicle_data.model"] ? responseErrors["vehicle_data.model"][0] : '',
                version: responseErrors["vehicle_data.version"] ? responseErrors["vehicle_data.version"][0] : '',
                year_manufacture: responseErrors["vehicle_data.year_manufacture"] ? responseErrors["vehicle_data.year_manufacture"][0] : '',
                vehicle_type: responseErrors["vehicle_data.vehicle_type"] ? responseErrors["vehicle_data.vehicle_type"][0] : '',
              });
              return;
            }

            setBlockingError(err.response?.data?.message || 'Não foi possível registrar a vistoria. Tente novamente.');
          }).finally(() => {
            setLoading(false);
          });
      });
  }

  const formAction = () => {
    setBlockingError('');

    const newErrors = {
      identification: !vesselFormData.identification?.trim() ? 'Identificação é obrigatória.' : '',
      brand: !vesselFormData.brand?.trim() ? 'Marca é obrigatória.' : '',
      model: !vesselFormData.model?.trim() ? 'Modelo é obrigatório.' : '',
      version: !vesselFormData.version?.trim() ? 'Versão é obrigatória.' : '',
      year_manufacture: !vesselFormData.year_manufacture?.trim() ? 'Ano é obrigatório.' : '',
      vehicle_type: !vesselFormData.vehicle_type?.trim() ? 'Tipo de embarcação é obrigatório.' : '',
    };

    const hasErrors = Object.values(newErrors).some((message) => !!message);

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    saveVehicleData();
  }

  return (
    <>
      <ManualFormLabel>Identificação da embarcação</ManualFormLabel>
      <InputIdentifier
        type="text"
        name="identification"
        maxLength={100}
        data-testid="input-identification"
        onChange={onChangeVesselData}
        placeholder="Digite o número de identificação da embarcação"
      />
      {errors.identification && (
        <ErrorMessage data-testid="error-identification">
          {errors.identification}
        </ErrorMessage>
      )}

      {showManualRegister && (
        <ManualForm>
          <ManualFormLabel>Marca</ManualFormLabel>
          <InputIdentifier
            type="text"
            name="brand"
            data-testid="input-brand"
            placeholder="Marca"
            onChange={onChangeVesselData}
          />
          {errors.brand && (
            <ErrorMessage>
              {errors.brand}
            </ErrorMessage>
          )}

          <ManualFormLabel>Modelo</ManualFormLabel>
          <InputIdentifier
            type="text"
            name="model"
            data-testid="input-model"
            placeholder="Modelo"
            onChange={onChangeVesselData}
          />
          {errors.model && (
            <ErrorMessage>
              {errors.model}
            </ErrorMessage>
          )}

          <ManualFormLabel>Versão</ManualFormLabel>
          <InputIdentifier
            type="text"
            name="version"
            data-testid="input-version"
            placeholder="Versão"
            onChange={onChangeVesselData}
          />
          {errors.version && (
            <ErrorMessage>
              {errors.version}
            </ErrorMessage>
          )}

          <ManualFormLabel>Ano</ManualFormLabel>
          <InputIdentifier
            name="year_manufacture"
            data-testid="input-year"
            placeholder="Ano"
            type="number"
            maxLength={4}
            onChange={onChangeVesselData}
          />
          {errors.year_manufacture && (
            <ErrorMessage>
              {errors.year_manufacture}
            </ErrorMessage>
          )}

          <ManualFormLabel>Tipo de embarcação</ManualFormLabel>
          <InputIdentifier
            type="text"
            name="vehicle_type"
            data-testid="input-vehicle-type"
            placeholder="Tipo de embarcação"
            onChange={onChangeVesselData}
          />
          {errors.vehicle_type && (
            <ErrorMessage data-testid="error-vehicle-type">
              {errors.vehicle_type}
            </ErrorMessage>
          )}
        </ManualForm>
      )}
      {blockingError && (
        <BlockingErrorMessage data-testid="blocking-error">
          {blockingError}
        </BlockingErrorMessage>
      )}

      <ConfirmButton
        type="button"
        onClick={formAction}
      >
        {!loading && ('Continuar compra')}
        {loading && (
          <CircularProgress sx={{ color: '#fff' }} size={23} />
        )}
      </ConfirmButton>
    </>
  );
}
